/**
 * The three auth endpoints (`040-api-contract.md` → Auth, `050-auth.md` → The endpoints).
 *
 * Hand-typed rather than generated: these are plain Symfony controllers /
 * security handlers, not API Platform operations, so they carry no OpenAPI schema
 * and never appear in `types.gen.ts` (`111-frontend-architecture.md` §6).
 */
import { ApiError, apiFetch, setCsrfToken } from '@/api/client';
import { forgetSession, rememberSession } from '@/stores/lastSession';

export interface User {
  id: string;
  username: string;
  displayName: string;
}

export interface SessionResponse {
  user: User;
  csrfToken: string;
}

/**
 * The app-boot call. The SPA cannot know whether it has a session — it cannot read
 * the httpOnly cookie — so it asks. 200 → render the shell. 401 → render /login.
 *
 * `raw`: a 401 here is the normal signed-out answer, not an expiry. It must not
 * fire the global 401 path (redirect + "your session expired" toast) on boot.
 */
export async function getSession(): Promise<SessionResponse | null> {
  try {
    const session = await apiFetch<SessionResponse>('/api/auth/me', { raw: true });
    setCsrfToken(session.csrfToken);
    rememberSession();
    return session;
  } catch (error) {
    if (isUnauthenticated(error)) {
      setCsrfToken(null);
      // A REAL 401 — the session is genuinely gone. (A network error falls through and
      // rethrows without touching the flag: the backend being down does not mean the user
      // is signed out, and forgetting here would refuse to boot the shell offline.)
      forgetSession();
      return null;
    }
    throw error;
  }
}

/** 401 on bad credentials, 429 + Retry-After when throttled. No CSRF token required. */
export async function login(username: string, password: string): Promise<SessionResponse> {
  const session = await apiFetch<SessionResponse>('/api/auth/login', {
    method: 'POST',
    body: JSON.stringify({ username, password }),
    raw: true, // a 401 here means "wrong password", not "session expired"
  });
  setCsrfToken(session.csrfToken);
  rememberSession();
  return session;
}

/**
 * 204, and idempotent — a logout that 401s is user-hostile. Requires CSRF
 * (050-auth.md: "POST /api/auth/logout is NOT exempt" — a forced logout is a nuisance
 * attack, but it is an attack).
 *
 * Not `raw`, deliberately: if a second tab signed in and rotated the session's token,
 * this 403s, and the standard refresh-and-retry-once path is exactly what should run.
 */
export async function logout(): Promise<void> {
  await apiFetch<void>('/api/auth/logout', { method: 'POST' });
  setCsrfToken(null);
  forgetSession();
}

function isUnauthenticated(error: unknown): boolean {
  return error instanceof ApiError && error.status === 401;
}
